import { ReactElement } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router';
import { Helmet } from 'react-helmet-async';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import AiCreateLocationPage from './pages/AiCreateLocationPage';
import CreateLocationPage from './pages/CreateLocationPage';
import LocationsPage from './pages/LocationsPage';
import DictionariesPage from './pages/DictionariesPage';
import AddDictionaryPage from './pages/AddDictionaryPage';
import UsersPage from './pages/UsersPage';
import CreateUserPage from './pages/CreateUserPage';
import EventsPage from './pages/EventsPage';
import CreateEventPage from './pages/CreateEventPage';
import NewsPage from './pages/NewsPage';
import CreateNewsPage from './pages/CreateNewsPage';
import RoutesPage from './pages/RoutesPage';
import CreateRoutePage from './pages/CreateRoutePage';
import ModerationPage from './pages/ModerationPage';
import ModerationItemPage from './pages/ModerationItemPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { isAuthenticated } from './lib/auth';

function RequireAuth({ children }: { children: ReactElement }) {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function EventViewRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get('id');
  if (id && /^\d+$/.test(id)) {
    return <Navigate to={`/events?view=${id}`} replace />;
  }
  return <Navigate to="/events" replace />;
}

function NewsViewRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get('id');
  if (id && /^\d+$/.test(id)) {
    return <Navigate to={`/news?view=${id}`} replace />;
  }
  return <Navigate to="/news" replace />;
}

function RouteViewRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get('id');
  if (id && /^\d+$/.test(id)) {
    return <Navigate to={`/routes?view=${id}`} replace />;
  }
  return <Navigate to="/routes" replace />;
}

function UnknownRouteRedirect() {
  if (isAuthenticated()) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/dashboard',
    element: (
      <RequireAuth>
        <DashboardPage />
      </RequireAuth>
    ),
  },
  {
    path: '/moderation',
    element: (
      <RequireAuth>
        <ModerationPage />
      </RequireAuth>
    ),
  },
  {
    path: '/moderation/:id',
    element: (
      <RequireAuth>
        <ModerationItemPage />
      </RequireAuth>
    ),
  },
  {
    path: '/dictionaries',
    element: (
      <RequireAuth>
        <DictionariesPage />
      </RequireAuth>
    ),
  },
  {
    path: '/dictionaries/create',
    element: (
      <RequireAuth>
        <AddDictionaryPage />
      </RequireAuth>
    ),
  },
  {
    path: '/locations',
    element: (
      <RequireAuth>
        <LocationsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/locations/create',
    element: (
      <RequireAuth>
        <CreateLocationPage />
      </RequireAuth>
    ),
  },
  {
    path: '/locations/view',
    element: <Navigate to="/locations" replace />,
  },
  {
    path: '/locations/ai-create',
    element: (
      <RequireAuth>
        <AiCreateLocationPage />
      </RequireAuth>
    ),
  },
  {
    path: '/users',
    element: (
      <RequireAuth>
        <UsersPage />
      </RequireAuth>
    ),
  },
  {
    path: '/users/create',
    element: (
      <RequireAuth>
        <CreateUserPage />
      </RequireAuth>
    ),
  },
  {
    path: '/news',
    element: (
      <RequireAuth>
        <NewsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/news/create',
    element: (
      <RequireAuth>
        <CreateNewsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/news/view',
    element: <NewsViewRedirect />,
  },
  {
    path: '/events',
    element: (
      <RequireAuth>
        <EventsPage />
      </RequireAuth>
    ),
  },
  {
    path: '/events/create',
    element: (
      <RequireAuth>
        <CreateEventPage />
      </RequireAuth>
    ),
  },
  {
    path: '/events/view',
    element: <EventViewRedirect />,
  },
  {
    path: '/routes',
    element: (
      <RequireAuth>
        <RoutesPage />
      </RequireAuth>
    ),
  },
  {
    path: '/routes/create',
    element: (
      <RequireAuth>
        <CreateRoutePage />
      </RequireAuth>
    ),
  },
  {
    path: '/routes/view',
    element: <RouteViewRedirect />,
  },
  {
    path: '*',
    element: <UnknownRouteRedirect />,
  },
]);

function App() {
  return (
    <>
      <Helmet>
        <title>MotoJourney</title>
        <meta name="description" content="MotoJourney admin application" />
      </Helmet>
      <RouterProvider router={router} />
    </>
  );
}

export default App;
